import React, { useState, useMemo } from 'react'
import { Trash, MagnetStraight, PlusCircle, Magnet } from '@phosphor-icons/react'
import { Button, Icon, IconButton } from '@shopflo/ui'
import CommonUtmSelectorDialog, {
  CommonUtmSelectorDialogUiConfig
} from 'src/components/common/utm/CommonUtmSelectorDialog'
import { useLocale } from 'src/lib/hooks/useLocal'
import { DEFAULT_UTM_OPTIONS } from 'src/components/common/utm/types'

import { CustomerEligibilityOptions, CustomerEligibilityStateType } from 'src/lib/types/common/customerConditions'
import { FlowErrorType } from 'src/lib/types/common/error'
import FormErrorText from 'src/components/common/FormErrorText'
import { uiConfig } from 'src/@core/configs/customer-eligibilty/customer-eligibility'

interface UtmParametersProps {
  state: Extract<CustomerEligibilityStateType, { type: CustomerEligibilityOptions.UTM_PARAMS }>
  onChange: (value: CustomerEligibilityStateType) => void
  error?: FlowErrorType
  description?: string
}

const UtmParameters: React.FC<UtmParametersProps> = ({ state, onChange, error, description }) => {
  const { utmData } = state
  const { t } = useLocale()
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const activeUtmEntries = Object.entries(utmData).filter(([, values]) => Boolean(values.length))

  const initialDataForDialog = useMemo(() => {
    const data: Record<string, string> = {}
    Object.entries(utmData).forEach(([key, values]) => {
      if (values.length) {
        data[key] = values.join(', ')
      }
    })
    return data
  }, [utmData])

  const handleSaveUtms = (data: Record<string, string>) => {
    const parsedValues: Record<string, string[]> = {}
    Object.entries(data).forEach(([key, value]) => {
      const values = value
        .split(',')
        .map(value => value.trim())
        .filter(Boolean)
      if (values.length) {
        parsedValues[key] = values
      }
    })
    onChange({ ...state, utmData: parsedValues })
    setIsDialogOpen(false)
  }

  const handleRemoveUtm = (key: string, e: React.MouseEvent) => {
    e.stopPropagation()
    const newUtmData = { ...utmData }
    delete newUtmData[key]
    onChange({ ...state, utmData: newUtmData })
  }

  const getUtmLabel = (key: string): string => {
    const option = DEFAULT_UTM_OPTIONS.find(option => option.value === key)
    if (option?.labelKey) return t(option.labelKey)
    return option?.label ?? key
  }

  return (
    <div className='pb-2'>
      <div className='flex flex-col gap-3 pb-3 border-b border-dotted border-zinc-200'>
        <CommonUtmSelectorDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          initialData={initialDataForDialog}
          onSave={handleSaveUtms}
          uiConfig={description ? { ...uiConfig, description } : uiConfig}
        />

        {activeUtmEntries.length === 0 ? (
          <div className='flex flex-col gap-1.5'>
            <Button
              id='add-utm-button'
              variant='dashed'
              leftIcon={PlusCircle}
              onClick={() => setIsDialogOpen(true)}
              className='w-fit bg-transparent'
            >
              {t('discounts_v2.add_utm')}
            </Button>
            {error?.status && <FormErrorText message={error.message} />}
          </div>
        ) : (
          <div className='flex flex-col gap-2'>
            <div className='flex items-stretch gap-2 w-full h-full'>
              <div className='border-y-1 border-l-1 border-zinc-200 w-5 rounded-l-lg h-auto'></div>
              <div className='flex flex-col py-2 rounded-2xl w-full h-full'>
                {activeUtmEntries.map(([key, values]) => (
                  <div
                    id={`edit-utm-${key}`}
                    key={key}
                    onClick={() => setIsDialogOpen(true)}
                    className='flex items-center justify-between py-1.5 rounded-xl'
                  >
                    <div className='flex items-center gap-1.5 overflow-hidden'>
                      <Icon
                        IconComponent={MagnetStraight}
                        size='xs'
                        className='text-zinc-400'
                        iconProps={{ weight: 'duotone' }}
                      />
                      <span className='text-text-sm-regular text-zinc-800 truncate'>{values.join(', ')}</span>
                    </div>

                    <div className='flex items-center gap-1.5 flex-shrink-0'>
                      <span className='text-text-xs-regular text-zinc-500 uppercase'>{getUtmLabel(key)}</span>
                      <IconButton
                        id={`remove-utm-${key}`}
                        IconComponent={Trash}
                        size='xs'
                        variant='ghost'
                        onClick={e => handleRemoveUtm(key, e)}
                      />
                    </div>
                  </div>
                ))}

                <div className='py-1.5'>
                  <Button
                    id='add-utm-button-footer'
                    variant='dashed'
                    leftIcon={PlusCircle}
                    onClick={() => setIsDialogOpen(true)}
                    className='w-fit bg-transparent '
                  >
                    {t('discounts_v2.add_utm')}
                  </Button>
                </div>
              </div>
              <div className='border-y-1 border-r-1 border-zinc-200 w-5 rounded-r-lg h-auto'></div>
            </div>
            {error?.status && <FormErrorText message={error.message} />}
          </div>
        )}
      </div>
    </div>
  )
}

export default UtmParameters
